/**
 * VH-114: Whole-session QR capture — public mobile-first page.
 *
 * Reached by scanning the SINGLE QR printed at the bottom of a workout sheet.
 * PUBLIC route `/capture/session/:token` — works without login; the token
 * scopes access to exactly one ProgramSession and its owner.
 *
 * It lists EVERY exercise in the session, each rendered with its correct
 * input template (set-based reps/weight vs metric-based fields like bike
 * duration/distance/avg_speed) via the shared `ExerciseCaptureBlock`, and
 * persists them all with a single Save. Only exercises the user actually
 * edits are sent (and thus stamped captured) — untouched exercises stay
 * unlogged so the session does not auto-complete prematurely (AC-9).
 */
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowsRightLeftIcon,
  ArrowUturnLeftIcon,
  ChevronRightIcon,
  NoSymbolIcon,
} from '@heroicons/react/24/outline'
import { getSessionCapture, saveSessionCapture } from '../../services/captureService'
import ExerciseCaptureBlock from './ExerciseCaptureBlock'
import ReportedBadge from './ReportedBadge'
import { exerciseProgress } from '../../utils/captureProgress'
import { useOptionalAuth } from '../../hooks/useAuth'
import { weeklyProgramsAPI } from '../../services/api'
import { apiErrorMessage } from '../../utils/apiError'
import { SubstituteExerciseModal } from '../workouts/SubstituteExerciseModal'
import { SessionLocationControl } from './SessionLocationControl'
import { SessionLocationPrompt } from './SessionLocationPrompt'
import MachineChoice from './MachineChoice'
import LoggedOutBar from './LoggedOutBar'
import { dropDraft, keepDraft, peekDraft } from '../../services/captureDraft'
import { loginEnded } from '../../services/loginReturn'
import {
  isBlockPerSet,
  buildSetRows,
  buildMetricValues,
  toNumberOrNull,
} from './exerciseCaptureUtils'

// Build the editable state for one load block (set rows OR metric values).
/** The name to show on a collapsed row, in the user's language. */
function blockTitle(block) {
  return block?.exercise_name || ''
}

/**
 * VH-148: what a collapsed row says about size — "3 sets" for a set-based
 * exercise, the metric count for one measured in duration/distance. Enough to
 * recognise the exercise without opening it.
 */
function summaryCount(block, t) {
  if (isBlockPerSet(block)) {
    const n = (block.sets || []).length
    return t('captureSession.setCount', { count: n, defaultValue: `${n} sets` })
  }
  const n = (block.metric_fields || []).length
  return t('captureSession.metricCount', { count: n, defaultValue: `${n} values` })
}

/**
 * VH-148: the next exercise still to report, after `afterId`. Derived from the
 * server's `is_captured` rather than stored, so a reload restores the position
 * for free — a session is reported across a whole workout, with the phone
 * locking in between.
 */
// VH-173: a SKIPPED exercise is settled — you decided not to do it — so it is
// not "still to report" and must not be auto-opened or advanced onto. Without
// this, skipping an exercise left it as the next thing the page pushed you back
// into, which reads as the skip not having worked.
const stillToReport = (b) => !b.is_captured && !b.is_skipped

function nextUnreportedId(blocks, afterId) {
  const list = blocks || []
  const start = list.findIndex((b) => b.session_exercise_id === afterId)
  const after = list.slice(start + 1).find(stillToReport)
  if (after) return after.session_exercise_id
  const any = list.find((b) => stillToReport(b) && b.session_exercise_id !== afterId)
  return any ? any.session_exercise_id : null
}

/** The exercise to open on load: the first one not yet reported. */
function firstUnreportedId(blocks) {
  const list = blocks || []
  const first = list.find(stillToReport)
  return first ? first.session_exercise_id : null
}

function initEdit(block) {
  return {
    sets: isBlockPerSet(block) ? buildSetRows(block.sets) : [],
    metricValues: isBlockPerSet(block) ? {} : buildMetricValues(block),
    // VH-206: the machine the athlete says they used. Kept beside the sets in
    // `edits` so a failed save preserves the answer exactly as it does the sets.
    // VH-207: when the page must ask, pre-answer with the machine this
    // location's variant remembers. The server only sends it while that machine
    // still stands there; otherwise the question is asked as usual.
    machineId:
      block.performed_equipment_id ??
      (block.needs_equipment_choice ? block.variant?.suggested_equipment_id : null) ??
      null,
  }
}

export default function CaptureSession() {
  const { token } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()

  // Editable state per exercise, keyed by session_exercise_id.
  const [edits, setEdits] = useState({})
  // Ids the user has actually touched since load — only these are saved.
  const [dirty, setDirty] = useState(() => new Set())
  const [saveResult, setSaveResult] = useState(null)
  // VH-114 B4: editable session date (ISO yyyy-mm-dd), seeded from the payload.
  const [sessionDate, setSessionDate] = useState('')
  // VH-120: local completion state — seeded from the loaded session and updated
  // from each save response (the public page has no query to invalidate). Drives
  // both the Mark Complete/Incomplete button label and the already-done banner.
  const [completed, setCompleted] = useState(false)
  // Guards the one-time seed of `edits` from the load payload (per token).
  const [seededToken, setSeededToken] = useState(null)
  // VH-148: which exercise is expanded. One at a time — allowing several
  // reproduces the long scroll the accordion exists to remove.
  const [openId, setOpenId] = useState(null)

  // VH-146: substituting mutates the PROGRAM, not just the values reported
  // against it. This page is reachable two ways — from "Report a session"
  // while logged in, and from a printed QR by anyone holding the token — and
  // only the first may change the plan. `user` is null for the QR visitor, so
  // the actions simply do not render for them; the endpoint is authenticated
  // regardless, this only keeps the UI honest about what it can do.
  const { user, loading: authLoading } = useOptionalAuth()
  const canEditPlan = Boolean(user)
  // VH-293: the login ended while this page stayed open (or before a reload).
  // A QR-only visitor never had a login here, so loginEnded() is false for them.
  const showLoggedOut = !user && !authLoading && loginEnded()
  const [substituting, setSubstituting] = useState(null)
  const [addingExercise, setAddingExercise] = useState(false)

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['captureSession', token],
    queryFn: () => getSessionCapture(token),
    retry: false,
  })

  const session = data?.data
  const locked = Boolean(session?.locked)
  const blocks = useMemo(() => session?.exercises || [], [session])

  // Seed the editable rows the moment the session payload arrives. Doing this
  // during render (guarded by the token) — rather than in an effect — means the
  // very first loaded render already carries the editable state, so there is no
  // one-frame gap where the exercises are missing.
  if (session && seededToken !== token) {
    const next = {}
    const restored = new Set()
    // VH-293: values typed before a new login come back over the server's.
    const draft = peekDraft(token)
    blocks.forEach((block) => {
      const id = block.session_exercise_id
      const kept = draft?.edits?.[id]
      next[id] = kept !== undefined ? kept : initEdit(block)
      if (kept !== undefined) restored.add(id)
    })
    setEdits(next)
    setDirty(restored)
    // VH-114 B4: seed the editable date from the loaded session (ISO or '').
    setSessionDate(draft?.sessionDate ?? (session.session_date || ''))
    // VH-120: seed local completion from the loaded session (B2 signal).
    setCompleted(Boolean(session.is_completed))
    // VH-148: open the first exercise still to report. On a fully reported
    // session nothing opens — the list reads as done.
    setOpenId(firstUnreportedId(blocks))
    setSeededToken(token)
  }

  // VH-293: the draft is put back by the seeding above; drop it afterwards.
  useEffect(() => {
    if (seededToken === token) dropDraft(token)
  }, [seededToken, token])

  // VH-293: keep what was typed, then log in and come back to this session.
  const loginAgain = () => {
    keepDraft(token, { edits, dirty, sessionDate })
    navigate('/login', { state: { returnTo: `/capture/session/${token}` } })
  }

  // VH-179: opening an exercise lands on its FIRST set.
  //
  // Reported from the train: expanding a block put the cursor at the LAST set.
  // Nothing here focused it — the browser keeps the toggle button in view while
  // the newly revealed content grows downward, so what you end up looking at is
  // the bottom of the block. You train sets in order, so the top is the only
  // sensible landing point.
  //
  // Scrolls, deliberately does NOT focus: focusing a number input on a phone
  // throws up the keyboard and hides the very rows the user just opened.
  useEffect(() => {
    if (openId == null) return
    const block = document.getElementById(`capture-block-${openId}`)
    if (!block) return
    // Let the expansion paint before measuring where it landed.
    const id = requestAnimationFrame(() => {
      // Guarded: jsdom does not implement scrollIntoView, and neither do a few
      // older mobile browsers. Positioning is a nicety — it must never be the
      // thing that throws while someone is logging a set.
      block.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' })
    })
    return () => cancelAnimationFrame(id)
  }, [openId])

  const markDirty = (id) => {
    setSaveResult(null)
    setDirty((prev) => {
      if (prev.has(id)) return prev
      const next = new Set(prev)
      next.add(id)
      return next
    })
  }

  const handleSetChange = (id, index, field, value) => {
    markDirty(id)
    setEdits((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        sets: prev[id].sets.map((s, i) => (i === index ? { ...s, [field]: value } : s)),
      },
    }))
  }

  const handleMetricChange = (id, key, value) => {
    markDirty(id)
    setEdits((prev) => ({
      ...prev,
      [id]: { ...prev[id], metricValues: { ...prev[id].metricValues, [key]: value } },
    }))
  }

  // VH-206: answering the machine question is an edit of that exercise.
  const handleMachineChange = (id, equipmentId) => {
    markDirty(id)
    setEdits((prev) => ({ ...prev, [id]: { ...prev[id], machineId: equipmentId } }))
  }

  // VH-206: the machine goes out ONLY when the page asked and the athlete
  // answered. Unasked → the server infers; skipped → "I don't remember".
  const machineField = (b, edit) =>
    b.needs_equipment_choice && edit.machineId != null
      ? { performed_equipment_id: edit.machineId }
      : {}

  // VH-120: `complete` explicitly drives session completion.
  //   null  → Save only (persist actuals, don't touch completion)
  //   true  → Mark Complete    false → Mark Incomplete
  const buildPayload = (complete = null) => {
    const exercises = blocks
      .filter((b) => dirty.has(b.session_exercise_id))
      .map((b) => {
        const edit = edits[b.session_exercise_id]
        if (isBlockPerSet(b)) {
          return {
            session_exercise_id: b.session_exercise_id,
            ...machineField(b, edit),
            sets: edit.sets.map((s) => ({
              set_number: s.set_number,
              set_type: s.set_type,
              actual_reps: toNumberOrNull(s.actual_reps),
              actual_weight: toNumberOrNull(s.actual_weight),
              weight_notation: s.weight_notation === '' ? null : s.weight_notation,
            })),
          }
        }
        const metric_values = {}
        Object.entries(edit.metricValues).forEach(([key, value]) => {
          if (value !== '' && value !== null && value !== undefined) {
            const n = Number(value)
            metric_values[key] = Number.isFinite(n) ? n : value
          }
        })
        return {
          session_exercise_id: b.session_exercise_id,
          ...machineField(b, edit),
          metric_values,
        }
      })
    // VH-114 B4: always carry the (possibly edited) session date so the backend
    // can update session.actual_date on save.
    return { exercises, session_date: sessionDate || null, complete }
  }

  // VH-146: after a swap the block is RELOADED from the server rather than
  // patched in place. Swapping a strength exercise for a cardio one changes the
  // whole input shape — sets become metric fields — and VH-139 records the
  // production bug from getting this wrong: substitution kept the replaced
  // exercise's set values. Re-seeding from the payload also picks up the new
  // exercise's own best-session prefill instead of the old one's.
  const substituteMutation = useMutation({
    mutationFn: ({ sessionExerciseId, newExerciseId }) =>
      weeklyProgramsAPI.substituteExercise(sessionExerciseId, { new_exercise_id: newExerciseId }),
    onSuccess: async () => {
      setSubstituting(null)
      // UAT 2026-09-15: re-seed only AFTER the fresh payload is in. Clearing the
      // guard first re-seeded `edits` from the still-cached pre-substitute
      // payload on the very next render, and the guard was already set again
      // when the refetch landed — so the card said "Leg Curl" over the squat's
      // rows, weights and targets.
      await refetch()
      setSeededToken(null)
    },
  })

  // VH-171: skip a planned exercise you did not do. Like substitute and add,
  // this mutates the PROGRAM rather than recording a value against it, so it is
  // authenticated-only — a QR-token holder records what happened, never changes
  // the plan (VH-146).
  const [skipError, setSkipError] = useState(null)
  const skipMutation = useMutation({
    mutationFn: ({ sessionExerciseId, skipped }) =>
      skipped
        ? weeklyProgramsAPI.skipExercise(sessionExerciseId, { reason: null })
        : weeklyProgramsAPI.unskipExercise(sessionExerciseId),
    onSuccess: () => { setSkipError(null); refetch() },
    onError: (error) =>
      setSkipError(apiErrorMessage(error, t('common.error', 'An error occurred'))),
  })

  // VH-156: add an exercise you did but had not planned. Goes through the
  // AUTHENTICATED client — the capture client deliberately never sends the JWT,
  // because a printed QR token may report values but must never change the plan.
  const addExerciseMutation = useMutation({
    mutationFn: (exerciseId) => weeklyProgramsAPI.addCaptureSessionExercise(token, exerciseId),
    onSuccess: async () => {
      setAddingExercise(false)
      // Re-seed after the refetch, never before — see substituteMutation.
      await refetch()
      setSeededToken(null)
    },
  })

  const mutation = useMutation({
    mutationFn: (payload) => saveSessionCapture(token, payload),
    onSuccess: (res, payload) => {
      const server = res?.data ?? {}
      const exerciseCount = server.exercise_count ?? payload.exercises.length
      const setCount =
        server.saved_count ??
        payload.exercises.reduce((sum, ex) => sum + (ex.sets?.length || 0), 0)
      setSaveResult({ ...server, exercise_count: exerciseCount, saved_count: setCount })
      // VH-120: the server returns the resulting completion state (unchanged for a
      // plain Save, toggled for Mark Complete/Incomplete) — it is authoritative.
      if (typeof server.session_completed === 'boolean') {
        setCompleted(server.session_completed)
      }
      // A successful save is "clean" again — re-opening/re-saving stays idempotent.
      setDirty(new Set())
      // VH-207: a save may have corrected an aim into a proven value on the
      // location variant — reload so the target marks reflect it.
      refetch()
    },
  })

  const handleSave = () => {
    if (locked || completed) return
    const payload = buildPayload(null)
    // VH-114 B4: a date-only change (no touched exercises) should still persist.
    const dateChanged = (sessionDate || '') !== (session.session_date || '')
    if (payload.exercises.length === 0 && !dateChanged) {
      // Nothing edited — no-op (empty session or untouched). Confirm harmlessly.
      setSaveResult({ exercise_count: 0, saved_count: 0, session_completed: completed })
      return
    }
    setSaveResult(null)
    mutation.mutate(payload)
  }

  // VH-120: dedicated Mark Complete / Mark Incomplete — always hits the server
  // (persisting any pending edits) even when nothing was touched, so the button
  // can complete an untouched session. Mirrors the web flow's explicit action.
  const handleToggleComplete = () => {
    if (locked) return
    setSaveResult(null)
    mutation.mutate(buildPayload(!completed))
  }

  // VH-295: undo a mistaken "Mark as completed". Logged-in owner, before the
  // week is submitted; reported values stay, the page is editable again.
  const reopenMutation = useMutation({
    mutationFn: () => weeklyProgramsAPI.reopenCaptureSession(token),
    onSuccess: () => {
      setCompleted(false)
      setSaveResult(null)
      refetch()
    },
  })
  const canReopen = canEditPlan && completed && !locked

  // --- Loading ---------------------------------------------------------------
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div
          role="status"
          aria-label={t('common.loading')}
          className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"
        />
      </div>
    )
  }

  // --- Invalid token (404) ---------------------------------------------------
  if (isError) {
    const status = error?.response?.status
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-sm w-full bg-surface-container rounded-xl shadow p-6 text-center">
          <h1 className="text-lg font-semibold text-gray-900 mb-2">
            {status === 404 ? t('capture.invalid.title') : t('capture.error.title')}
          </h1>
          <p className="text-sm text-gray-600">
            {status === 404 ? t('capture.invalid.message') : t('capture.error.message')}
          </p>
        </div>
      </div>
    )
  }

  const sessionName = session.module_name || session.session_name || ''
  // VH-124 Fix 3: a completed session is LOCKED — read-only and not
  // re-capturable — exactly like a finalized (program-locked) session. VH-295:
  // the logged-in owner may reopen it here until the week is submitted.
  const isLocked = locked || completed

  // --- Capture form ----------------------------------------------------------
  return (
    <div className="min-h-screen bg-gray-50 pb-28">
      <div className="max-w-md mx-auto px-4 pt-6">
        {showLoggedOut && <LoggedOutBar onLogin={loginAgain} />}
        {/* Header */}
        <header className="mb-5">
          {/* VH-179: a way back to the training menu.
              Reported: once inside a session report there was no exit but the
              browser gesture. Shown only to a SIGNED-IN user — a QR-token
              holder has no menu to return to, and sending them to /log-session
              would land them on a login screen, which reads as the app breaking
              rather than as a boundary. */}
          {canEditPlan && (
            <button
              type="button"
              onClick={() =>
                window.history.length > 1 ? navigate(-1) : navigate('/log-session')
              }
              className="mb-3 inline-flex items-center text-sm text-gray-500 hover:text-gray-900"
              data-testid="capture-back"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              {t('common.back', 'Back')}
            </button>
          )}
          <h1 className="text-xl font-bold text-gray-900">{sessionName}</h1>
          <p className="mt-1 text-sm text-gray-600">
            {t('capture.weekLabel', { week: session.week_number })}
          </p>
          {/* VH-114 B4: editable session date (pre-filled from session_date). */}
          <div className="mt-3">
            <label
              htmlFor="capture-session-date"
              className="mb-1 block text-xs font-medium text-gray-600"
            >
              {t('capture.date')}
            </label>
            <input
              id="capture-session-date"
              type="date"
              value={sessionDate}
              disabled={isLocked}
              onChange={(e) => {
                setSaveResult(null)
                setSessionDate(e.target.value)
              }}
              className="w-full px-3 py-2 text-base focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-100 disabled:text-gray-500"
            />
          </div>
          {/* VH-212: where the athlete actually is. Shown only to a signed-in
              user, like the other plan-changing controls — a QR-token holder
              reports values and does not restate where the training happened.
              Changing it re-scopes the pickers below for the rest of the
              session; `refetch` pulls the server's RESOLVED answer so clearing
              the override re-renders as the module's location rather than as
              nothing. */}
          {canEditPlan && !isLocked && (
            <SessionLocationControl
              token={token}
              locationId={session?.location_id}
              locationName={session?.location_name}
              isOverride={Boolean(session?.location_is_override)}
              onChanged={() => refetch()}
            />
          )}
        </header>

        {/* VH-219: ask where the athlete is, once, before logging. The dropdown
            above is easy to scroll past at an away gym, and VH-206's machine
            question and VH-207's prefilled numbers both follow this value.
            Same eligibility boundary as the control. Keyed on the resolved
            location so the pre-selection is right even if the session loads
            after first render. */}
        {canEditPlan && !isLocked && session && (
          <SessionLocationPrompt
            key={`${token}-${session.location_id ?? 'none'}`}
            token={token}
            locationId={session.location_id}
            isOverride={Boolean(session.location_is_override)}
            onDone={() => refetch()}
          />
        )}

        {/* VH-124 Fix 3: completed → success confirmation + locked notice. A
            completed session is read-only here and cannot be re-opened or
            re-captured from this public page. */}
        {completed && (
          <div
            role="status"
            className="mb-4 rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800"
          >
            <p className="font-semibold">{t('capture.completedSuccess')}</p>
            <p className="mt-1 font-medium">
              {canReopen ? t('captureSession.reopen.hint') : t('capture.completedAndLocked')}
            </p>
            {canReopen && (
              <button
                type="button"
                onClick={() => reopenMutation.mutate()}
                disabled={reopenMutation.isPending}
                className="mt-3 inline-flex w-full min-h-[44px] items-center justify-center gap-2 rounded-lg border border-green-300 bg-surface-container px-4 py-2 font-semibold text-green-800 hover:bg-green-100 disabled:opacity-50"
                data-testid="capture-reopen"
              >
                <ArrowUturnLeftIcon className="h-5 w-5" aria-hidden="true" />
                {t('captureSession.reopen.button')}
              </button>
            )}
            {reopenMutation.isError && (
              <p className="mt-2 text-red-700" role="alert" data-testid="capture-reopen-error">
                {t('captureSession.reopen.failed')}
              </p>
            )}
          </div>
        )}

        {/* Finalized (read-only) banner */}
        {locked && (
          <div
            role="status"
            className="mb-4 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm font-medium text-amber-800"
          >
            {t('capture.lockedBanner')}
          </div>
        )}

        {/* Empty session */}
        {blocks.length === 0 && (
          <div className="rounded-lg bg-surface-container shadow-sm px-4 py-8 text-center text-sm text-gray-600">
            {t('captureSession.empty')}
          </div>
        )}

        {/* VH-156: report something you did that was not planned. Authenticated
            only, and hidden once the program is SUBMITTED — same rule as
            substitute. */}
        {canEditPlan && !isLocked && (
          <button
            type="button"
            onClick={() => setAddingExercise(true)}
            data-testid="capture-add-exercise"
            className="mb-4 w-full rounded-lg border border-dashed border-outline-variant px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-100"
          >
            + {t('programs.addExercise', { defaultValue: 'Add exercise' })}
          </button>
        )}

        {/* One block per exercise */}
        <div className="space-y-4">
          {blocks.map((block) => {
            const id = block.session_exercise_id
            const edit = edits[id]
            if (!edit) return null
            // VH-148: on a phone one exercise is already taller than the
            // screen, so an 8-exercise session was a scroll with no landmarks —
            // Reto had to start from the top to find the third exercise. Only
            // the open one renders its inputs; the rest are summary rows.
            //
            // Collapsing cannot lose typed values: `edits` lives here, in the
            // parent, and the block is a controlled child. Unmounting it is
            // safe, and keeps the DOM small on a long session.
            const isOpen = id === openId
            const progress = exerciseProgress(block)
            // VH-140: the badge lives inside the block header now (next to the
            // exercise name, where the eye already is). The old right-aligned
            // grey line only ever said "not logged" and said nothing at all
            // once something WAS logged, which is the half Reto needed.
            //
            // Unsaved local edits are deliberately not treated as reported:
            // a typed number must never look like a stored one.
            return (
              // VH-175: a skipped exercise is dimmed and struck through, the
              // same treatment ExerciseCard gives it on desktop. Matching the
              // desktop rather than inventing a second visual language: the two
              // screens describe the same rows, and a user who learns one
              // should not have to learn the other.
              <div
                key={id}
                className={`rounded-lg shadow-sm overflow-hidden ${
                  block.is_skipped
                    ? 'bg-gray-100 opacity-60'
                    : 'bg-surface-container'
                }`}
              >
                <button
                  type="button"
                  onClick={() => setOpenId(isOpen ? null : id)}
                  aria-expanded={isOpen}
                  aria-controls={`capture-block-${id}`}
                  data-testid={`capture-summary-${id}`}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500"
                >
                  <ChevronRightIcon
                    className={`h-4 w-4 flex-shrink-0 text-gray-500 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                  />
                  {/* VH-179: the name WRAPS, it is not truncated.
                      Reported from a phone: exercises exist in variations, and
                      the part that distinguishes them sits at the END of the
                      name — "Incline Bench Press (narrow grip)" cut to
                      "Incline Bench Pre…" is not a name, it is a guess. The row
                      is the only thing visible for the exercise you are about
                      to train, so two lines of certainty beat one line of
                      tidiness. */}
                  <span
                    className={`flex-1 min-w-0 font-medium leading-snug break-words ${
                      block.is_skipped ? 'text-gray-500 line-through' : 'text-gray-900'
                    }`}
                  >
                    {blockTitle(block)}
                  </span>
                  {/* VH-173: a skipped exercise is collapsed, so this row is
                      the only thing the user sees of it. Without the label the
                      skip is invisible and reads as not having worked. */}
                  {block.is_skipped && (
                    <span
                      className="flex-shrink-0 rounded bg-gray-200 px-2 py-0.5 text-xs font-semibold text-gray-700"
                      data-testid={`capture-skipped-${id}`}
                    >
                      {t('programs.skipped')}
                    </span>
                  )}
                  {block.is_substitute && (
                    <span className="flex-shrink-0 text-xs text-gray-500">
                      {t('capture.substitute', { defaultValue: 'Substitute' })}
                    </span>
                  )}
                  <span className="flex-shrink-0 text-xs text-gray-500">
                    {summaryCount(block, t)}
                  </span>
                  <ReportedBadge
                    state={progress.state}
                    reported={progress.reported}
                    total={progress.total}
                  />
                </button>

                {isOpen && (
                  <div id={`capture-block-${id}`} className="border-t border-outline-variant">
                    {/* VH-206: asked ONLY on the server's say-so — never
                        derived from the candidate count here, so the one rule
                        lives in one place (session_equipment_resolver). */}
                    {/* VH-206 BUG-7: the recorded machine was removed since —
                        say so instead of offering it as a normal machine. */}
                    {block.performed_equipment_unavailable && !block.is_skipped && (
                      <p
                        className="px-4 pt-3 text-xs text-orange-700"
                        data-testid={`capture-machine-unavailable-${id}`}
                      >
                        {t('machineChoice.unavailable', { name: block.performed_equipment_name })}
                      </p>
                    )}
                    {block.needs_equipment_choice && !block.is_skipped && (
                      <MachineChoice
                        candidates={block.equipment_candidates || []}
                        value={edit.machineId}
                        disabled={isLocked}
                        onChange={(equipmentId) => handleMachineChange(id, equipmentId)}
                        testIdPrefix={`capture-machine-${id}`}
                      />
                    )}
                    {/* VH-207: a remembered substitute is OFFERED, never
                        applied — taking it goes through the same substitute
                        call as the picker. */}
                    {canEditPlan && !isLocked && !block.is_substitute && !block.is_skipped &&
                      block.variant?.suggested_substitute_exercise_id && (
                      <div
                        className="mx-4 mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-primary-200 bg-primary-50 px-3 py-2 text-sm"
                        data-testid={`capture-variant-substitute-${id}`}
                      >
                        <span className="flex-1 min-w-0 text-gray-700">
                          {t('variants.offerSubstitute', {
                            name: block.variant.suggested_substitute_exercise_name,
                          })}
                        </span>
                        <button
                          type="button"
                          disabled={substituteMutation.isPending}
                          onClick={() => {
                            const reported = exerciseProgress(block).reported
                            // Substituting deletes reported sets — never silently.
                            if (reported > 0 && !window.confirm(t('programs.substituteDeletesReported', {
                              count: reported, name: block.exercise_name,
                            }))) return
                            substituteMutation.mutate({
                              sessionExerciseId: id,
                              newExerciseId: block.variant.suggested_substitute_exercise_id,
                            })
                          }}
                          data-testid={`capture-variant-substitute-use-${id}`}
                          className="rounded-lg bg-primary-600 px-3 py-1 text-xs font-medium text-on-primary hover:bg-primary-700 disabled:opacity-50"
                        >
                          {t('variants.useSubstitute', {
                            name: block.variant.suggested_substitute_exercise_name,
                          })}
                        </button>
                      </div>
                    )}
                    <ExerciseCaptureBlock
                      block={block}
                      locked={isLocked}
                      sets={edit.sets}
                      metricValues={edit.metricValues}
                      onSetChange={(index, field, value) => handleSetChange(id, index, field, value)}
                      onMetricChange={(key, value) => handleMetricChange(id, key, value)}
                    />
                    {/* VH-148: advancing is the whole point — finishing one
                        exercise should land on the next one still to do, not
                        send you back to hunt for it. Not a save: VH-114 keeps
                        one all-or-nothing request at the end. */}
                    {/* VH-294: Substitute and Skip are the exceptions, side by
                        side and outlined with an icon; "Next exercise" is the
                        usual way on, filled and full width below. ≥44 px for
                        thumbs mid-workout. */}
                    <div className="px-4 pb-4 space-y-2">
                      {canEditPlan && !isLocked && (
                        <div className="flex gap-2">
                          {/* VH-146: the machine is occupied and you need another
                              exercise NOW. Only for a signed-in user — see
                              canEditPlan. Hidden once locked, because a SUBMITTED
                              program is finalized. */}
                          <button
                            type="button"
                            onClick={() => setSubstituting(block)}
                            data-testid={`capture-substitute-${id}`}
                            className="flex-1 min-h-[44px] inline-flex items-center justify-center gap-1.5 rounded-lg border border-primary-300 bg-surface-container px-3 py-2 text-sm font-medium text-primary-700 hover:bg-primary-50"
                          >
                            <ArrowsRightLeftIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                            {t('captureSession.substituteShort', { defaultValue: 'Substitute' })}
                          </button>
                          {/* VH-171: you planned it and did not do it. Same rule
                              as substitute — a program change, so signed-in only,
                              and hidden once the program is SUBMITTED. */}
                          <button
                            type="button"
                            onClick={() =>
                              skipMutation.mutate({
                                sessionExerciseId: id,
                                skipped: !block.is_skipped,
                              })
                            }
                            disabled={skipMutation.isPending}
                            data-testid={`capture-skip-${id}`}
                            className="flex-1 min-h-[44px] inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-300 bg-surface-container px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                          >
                            {block.is_skipped ? (
                              <ArrowUturnLeftIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                            ) : (
                              <NoSymbolIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                            )}
                            {block.is_skipped
                              ? t('captureSession.unskipExercise', { defaultValue: 'Did do it' })
                              : t('captureSession.skipExercise', { defaultValue: 'Skip' })}
                          </button>
                        </div>
                      )}
                      {nextUnreportedId(blocks, id) != null && (
                        <button
                          type="button"
                          onClick={() => setOpenId(nextUnreportedId(blocks, id))}
                          data-testid={`capture-next-${id}`}
                          className="w-full min-h-[44px] inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-on-primary hover:bg-primary-700"
                        >
                          {t('captureSession.nextExercise', { defaultValue: 'Next exercise' })}
                          <ArrowRightIcon className="h-5 w-5" aria-hidden="true" />
                        </button>
                      )}
                    </div>
                    {skipError && canEditPlan && (
                      <p className="px-4 pb-3 text-sm text-red-600" data-testid="capture-skip-error">
                        {skipError}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* VH-146: the picker is shared with the program view rather than
            duplicated, so the scoping rules (and the production fixes baked
            into them) stay in one place. `locationId` scopes it to where the
            session actually happens; until the capture payload carries the
            location it is simply undefined and the picker shows everything,
            which is the same behaviour the program view has today. */}
        {/* VH-298: a picker left open when the login ends closes; the
            logged-out bar takes its place. */}
        {addingExercise && canEditPlan && (
          <SubstituteExerciseModal
            exercise={{ isNewActivity: true }}
            locationId={session?.location_id}
            locationName={session?.location_name}
            isLoading={addExerciseMutation.isPending}
            onClose={() => setAddingExercise(false)}
            onConfirm={(picked) => addExerciseMutation.mutate(picked.id)}
          />
        )}

        {substituting && canEditPlan && (
          <SubstituteExerciseModal
            exercise={{
              snapshot_exercise_name_en: substituting.exercise_name,
              snapshot_exercise_type: substituting.exercise_type,
              snapshot_muscle_group: substituting.muscle_group,
            }}
            locationId={session?.location_id}
            locationName={session?.location_name}
            isLoading={substituteMutation.isPending}
            reportedSetCount={exerciseProgress(substituting).reported}
            onClose={() => setSubstituting(null)}
            onConfirm={(picked) =>
              substituteMutation.mutate({
                sessionExerciseId: substituting.session_exercise_id,
                newExerciseId: picked.id,
              })
            }
          />
        )}

        {/* Inline confirmation / error */}
        {saveResult && (
          <div className="mt-4 rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800">
            <p className="font-medium">
              {t('captureSession.saved', {
                exercises: saveResult.exercise_count,
                sets: saveResult.saved_count,
              })}
            </p>
            {saveResult.session_completed && <p className="mt-1">{t('capture.sessionCompleted')}</p>}
          </div>
        )}
        {/* Non-blocking validation warnings (still saved). */}
        {saveResult?.warnings?.length > 0 && (
          <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
            <p className="font-medium">{t('capture.warningsTitle')}</p>
            <ul className="mt-1 list-disc list-inside space-y-1">
              {saveResult.warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </div>
        )}
        {mutation.isError && (
          <div className="mt-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
            {mutation.error?.response?.status === 409
              ? t('capture.finalizedError')
              : t('capture.saveError')}
          </div>
        )}
      </div>

      {/* VH-120: sticky action bar — Save (persist only) + a dedicated Mark
          Complete, mirroring the web logging flow. VH-124 Fix 3: once the session
          is completed it is locked, so the bar (and any re-open control) is
          removed — the completion is final on this public page. */}
      {!completed && (
        <div className="fixed bottom-0 inset-x-0 bg-surface-container border-t border-gray-200 px-4 py-3">
          <div className="max-w-md mx-auto flex gap-3">
            <button
              type="button"
              onClick={handleSave}
              disabled={mutation.isPending || locked}
              className="flex-1 rounded-lg border border-gray-300 bg-surface-container text-gray-800 font-semibold py-3 text-base disabled:opacity-60 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              {mutation.isPending ? t('capture.saving') : t('capture.save')}
            </button>
            <button
              type="button"
              onClick={handleToggleComplete}
              disabled={mutation.isPending || locked}
              className="flex-1 rounded-lg bg-green-600 text-on-neutral font-semibold py-3 text-base disabled:opacity-60 disabled:cursor-not-allowed hover:bg-green-700"
            >
              {mutation.isPending ? t('capture.saving') : t('capture.markComplete')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
